(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-egifts-cu-upsell]';
  const media = window.matchMedia('(max-width: 749px)');

  const route = (path) => {
    const root = String(window.Shopify?.routes?.root || '/');
    return `${root}${String(path || '').replace(/^\//, '')}`;
  };

  const fetchCart = async () => {
    const response = await fetch(route('cart.js'), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart HTTP ${response.status}`);
    return response.json();
  };

  const currentProductId = (root) => String(root?.dataset.currentProductId || '').replace(/\D/g, '');
  const cartLineProductId = (item) => String(item?.product_id || '').replace(/\D/g, '');
  const cartLineQuantity = (item) => Number(item?.quantity || 0);

  const mainLines = (root, cart) => {
    const id = currentProductId(root);
    if (!id) return [];
    return (cart?.items || []).filter((item) => cartLineProductId(item) === id);
  };

  const snapshotMainLines = (root, cart) => {
    const result = new Map();
    mainLines(root, cart).forEach((item) => result.set(String(item.key || ''), cartLineQuantity(item)));
    return result;
  };

  const primeBaseline = async (root) => {
    if (root._egiftsCuBaselinePromise) return root._egiftsCuBaselinePromise;
    root._egiftsCuBaselinePromise = fetchCart()
      .then((cart) => {
        root._egiftsCuBaseline = snapshotMainLines(root, cart);
        return root._egiftsCuBaseline;
      })
      .catch(() => {
        root._egiftsCuBaseline = new Map();
        return root._egiftsCuBaseline;
      });
    return root._egiftsCuBaselinePromise;
  };

  const findSessionMainLine = (root, cart) => {
    const preferredKey = String(root._egiftsCuMainLineKey || '');
    const lines = mainLines(root, cart);

    if (preferredKey) {
      const preferred = lines.find((item) => String(item.key || '') === preferredKey);
      if (preferred) return preferred;
    }

    const baseline = root._egiftsCuBaseline instanceof Map ? root._egiftsCuBaseline : new Map();
    return lines.find((item) => {
      const key = String(item.key || '');
      return !baseline.has(key) || cartLineQuantity(item) > Number(baseline.get(key) || 0);
    }) || null;
  };

  const getSessionMainLine = async (root) => {
    await primeBaseline(root);
    const cart = await fetchCart();
    const line = findSessionMainLine(root, cart);
    if (line?.key) root._egiftsCuMainLineKey = String(line.key);
    return line || null;
  };

  const mainSubmitButton = (root) => {
    const sectionId = String(root?.dataset.currentSectionId || '');
    return document.getElementById(`ProductSubmitButton-${sectionId}`)
      || document.querySelector(`product-form[data-section-id="${CSS.escape(sectionId)}"] [name="add"]`)
      || document.querySelector('main product-form [name="add"]');
  };

  const scrollToMainProduct = (root) => {
    const sectionId = String(root?.dataset.currentSectionId || '');
    const mainProduct = document.getElementById(`MainProduct-${sectionId}`)
      || document.querySelector(`product-info[data-section="${CSS.escape(sectionId)}"]`)
      || document.querySelector('product-info');
    if (!mainProduct) return;

    const target = mainProduct.querySelector?.('.product__info-wrapper')
      || mainProduct.querySelector?.('.product__info-container')
      || mainProduct;

    const header = document.querySelector('sticky-header, .header-wrapper');
    const headerRect = header?.getBoundingClientRect?.();
    const headerHeight = headerRect && headerRect.height > 0 ? Math.min(headerRect.height, 150) : 0;
    const fallbackOffset = media.matches ? 92 : 72;
    const offset = Math.max(fallbackOffset, headerHeight + 16);
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);

    window.scrollTo({ top, behavior: 'smooth' });
  };

  const focusMainProductAdd = (root) => {
    scrollToMainProduct(root);
    const submit = mainSubmitButton(root);
    if (!submit) return;

    window.setTimeout(() => {
      try {
        submit.focus({ preventScroll: true });
      } catch (error) {
        try { submit.focus(); } catch (ignore) {}
      }
      submit.classList.add('egifts-cu-main-add-highlight');
      window.setTimeout(() => submit.classList.remove('egifts-cu-main-add-highlight'), 1800);
    }, 420);
  };

  const closeModal = (modal, resolve, value) => {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('egifts-cu-modal-open');
    window.setTimeout(() => modal.remove(), 160);
    if (typeof resolve === 'function') resolve(value);
  };

  const showModal = ({
    title,
    body = '',
    warning = '',
    primaryLabel = 'OK',
    primaryValue = 'primary',
    secondaryLabel = 'Anuluj',
    secondaryValue = 'cancel',
    secondaryTone = 'secondary'
  }) => new Promise((resolve) => {
    const modal = document.createElement('div');
    modal.className = 'egifts-cu-modal';
    modal.innerHTML = `
      <div class="egifts-cu-modal__backdrop" data-egifts-cu-modal-close></div>
      <div class="egifts-cu-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="EgiftsCuModalTitle" tabindex="-1">
        <button type="button" class="egifts-cu-modal__close" data-egifts-cu-modal-close aria-label="Zamknij">×</button>
        <span class="egifts-cu-modal__icon" aria-hidden="true">!</span>
        <h3 id="EgiftsCuModalTitle">${escapeHtml(title)}</h3>
        ${body ? `<p class="egifts-cu-modal__text">${escapeHtml(body)}</p>` : ''}
        ${warning ? `<p class="egifts-cu-modal__warning">${escapeHtml(warning)}</p>` : ''}
        <div class="egifts-cu-modal__actions">
          <button type="button" class="egifts-cu-modal__button is-primary" data-egifts-cu-modal-value="${escapeHtml(primaryValue)}">${escapeHtml(primaryLabel)}</button>
          ${secondaryLabel ? `<button type="button" class="egifts-cu-modal__button is-${escapeHtml(secondaryTone)}" data-egifts-cu-modal-value="${escapeHtml(secondaryValue)}">${escapeHtml(secondaryLabel)}</button>` : ''}
        </div>
      </div>`;

    let finished = false;
    const finish = (value) => {
      if (finished) return;
      finished = true;
      document.removeEventListener('keydown', onKeyDown, true);
      closeModal(modal, resolve, value);
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      finish('cancel');
    };

    modal.addEventListener('click', (event) => {
      const action = event.target.closest?.('[data-egifts-cu-modal-value]');
      if (action) {
        event.preventDefault();
        finish(String(action.dataset.egiftsCuModalValue || 'cancel'));
        return;
      }

      const clickedClose = event.target.closest?.('[data-egifts-cu-modal-close]');
      const clickedOutside = !event.target.closest?.('.egifts-cu-modal__dialog');
      if (clickedClose || clickedOutside) {
        event.preventDefault();
        finish('cancel');
      }
    });

    document.addEventListener('keydown', onKeyDown, true);
    document.body.append(modal);
    document.documentElement.classList.add('egifts-cu-modal-open');

    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      modal.querySelector('.egifts-cu-modal__dialog')?.focus();
    });
  });

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const confirmLeave = async (root, url) => {
    const mainLine = await getSessionMainLine(root);
    const hasMain = Boolean(mainLine?.key);

    const first = await showModal({
      title: hasMain ? 'Personalizacja otworzy się na osobnej karcie' : 'Najpierw dodaj ten produkt do koszyka',
      body: hasMain
        ? 'Oglądany produkt jest już w koszyku, więc możesz bezpiecznie przejść do personalizacji dodatkowego produktu.'
        : 'Jeśli chcesz zachować oglądany produkt i ustawioną tutaj personalizację, dodaj go do koszyka przed przejściem do kolejnego produktu.',
      warning: hasMain
        ? 'WAŻNE: dodatkowy produkt wymaga opuszczenia obecnej karty i otworzy się na nowej stronie.'
        : 'WAŻNE: niczego nie dodajemy automatycznie. Przejście dalej bez dodania produktu może oznaczać utratę ustawionej tutaj personalizacji.',
      primaryLabel: hasMain ? 'Przejdź do personalizacji' : 'Wróć i dodaj ten produkt',
      primaryValue: hasMain ? 'leave' : 'add-main',
      secondaryLabel: hasMain ? 'Zostań tutaj' : 'Przejdź mimo to',
      secondaryValue: hasMain ? 'cancel' : 'leave-anyway',
      secondaryTone: hasMain ? 'secondary' : 'danger'
    });

    if (first === 'add-main') {
      focusMainProductAdd(root);
      return;
    }
    if (first === 'leave') {
      window.location.assign(url);
      return;
    }
    if (first !== 'leave-anyway') return;

    const second = await showModal({
      title: 'Czy na pewno chcesz opuścić tę stronę?',
      body: 'Oglądany produkt nadal nie został dodany do koszyka.',
      warning: 'Możesz stracić ustawioną tutaj personalizację. Jeśli chcesz ten produkt, najbezpieczniej najpierw dodaj go do koszyka.',
      primaryLabel: 'Zostań i dodaj produkt',
      primaryValue: 'add-main',
      secondaryLabel: 'Tak, przejdź bez dodania',
      secondaryValue: 'leave',
      secondaryTone: 'danger'
    });

    if (second === 'add-main') {
      focusMainProductAdd(root);
      return;
    }
    if (second === 'leave') window.location.assign(url);
  };

  const handleOpen = async (event) => {
    const trigger = event.target.closest?.('[data-egifts-cu-open]');
    if (!trigger) return;

    const root = trigger.closest(ROOT_SELECTOR);
    if (!root) return;

    const url = String(trigger.dataset.productUrl || trigger.getAttribute('href') || '');
    if (!url) return;

    event.preventDefault();
    try {
      await confirmLeave(root, url);
    } catch (error) {
      console.warn('[E-GIFTS] Customily category upsell:', error);
    }
  };

  const initRoot = (root) => {
    if (!root || root.dataset.egiftsCuUpsellReady === 'true') return;
    root.dataset.egiftsCuUpsellReady = 'true';
    if (!root.querySelector('.egifts-cu-upsell__item')) {
      root.hidden = true;
      return;
    }
    primeBaseline(root).catch(() => {});
  };

  const init = () => document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);

  document.addEventListener('click', handleOpen, true);
  document.addEventListener('DOMContentLoaded', init, { once: true });
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();